import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Trash2,
  Shield,
  ArrowRight,
  Zap,
  BookOpen,
  FlaskConical,
  Feather,
  Image,
} from "lucide-react";

const DeleteAccounts = () => {
  const apps = [
    {
      name: "Shayari App",
      description: "Delete your Shayari account and all posted content.",
      icon: Feather,
      color: "bg-purple-100 text-purple-600",
      link: "/shayari/deleteAccount",
    },
    {
      name: "Chemistry App",
      description: "Remove your Chemistry learning profile and progress.",
      icon: FlaskConical,
      color: "bg-blue-100 text-blue-600",
      link: "/chemistry/deleteAccount",
    },
    {
      name: "Wallpaper App",
      description: "Delete your Wallpaper collection and preferences.",
      icon: Image,
      color: "bg-pink-100 text-pink-600",
      link: "/wallpaper/deleteAccount",
    },
    {
      name: "GK Quiz App",
      description: "Erase your General Knowledge quiz scores and history.",
      icon: BookOpen,
      color: "bg-orange-100 text-orange-600",
      link: "/gk/deleteAccount",
    },
    {
      name: "TestSetu App",
      description: "Delete your main TestSetu account and data.",
      icon: Zap,
      color: "bg-indigo-100 text-indigo-600",
      link: "/testsetu/deleteAccount",
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50 px-4 py-12 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-4xl">
        <div className="mb-12 text-center">
          <div className="mb-4 flex justify-center">
            <div className="rounded-full bg-red-100 p-4">
              <Trash2 className="h-8 w-8 text-red-600" />
            </div>
          </div>
          <h1 className="text-4xl font-extrabold tracking-tight text-gray-900 sm:text-5xl">
            Account Deletion Center
          </h1>
          <p className="mx-auto mt-4 max-w-2xl text-xl text-gray-600">
            Manage the deletion of your accounts across the TestSetu ecosystem.
            Please note that these actions are permanent.
          </p>
        </div>

        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {apps.map((app, index) => (
            <Link to={app.link} key={index} className="group">
              <motion.div
                whileHover={{ y: -5 }}
                className="flex h-full flex-col rounded-2xl border border-gray-100 bg-white p-6 shadow-sm transition-all duration-300 hover:shadow-xl"
              >
                <div
                  className={`mb-4 flex h-12 w-12 items-center justify-center rounded-xl ${app.color}`}
                >
                  <app.icon size={24} />
                </div>
                <h3 className="mb-2 text-lg font-bold text-gray-900 transition-colors group-hover:text-red-600">
                  {app.name}
                </h3>
                <p className="mb-6 flex-grow text-sm text-gray-500">
                  {app.description}
                </p>
                <div className="mt-auto flex items-center text-sm font-medium text-gray-400 transition-colors group-hover:text-red-600">
                  Proceed to Delete
                  <ArrowRight
                    size={16}
                    className="ml-2 transition-transform group-hover:translate-x-1"
                  />
                </div>
              </motion.div>
            </Link>
          ))}
        </div>

        <div className="mt-16 rounded-2xl border border-blue-100 bg-blue-50 p-8">
          <div className="flex items-start gap-4">
            <Shield className="mt-1 h-6 w-6 flex-shrink-0 text-blue-600" />
            <div>
              <h3 className="mb-2 text-lg font-semibold text-blue-900">
                Data Privacy & Security
              </h3>
              <p className="leading-relaxed text-blue-700">
                At TestSetu, we take your data privacy seriously. When you
                delete an account, all associated personal data is permanently
                removed from our servers in accordance with our Privacy Policy
                and data protection regulations. This process cannot be undone.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeleteAccounts;
